<template>
  <div class="container py-4">
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
      <h1 class="h3 fw-semibold mb-2 mb-md-0">Overview</h1>
      <button class="btn btn-outline-dark" @click="openCreateModal">New Order Request</button>
    </div>

    <div class="row g-3 mb-4">
      <div class="col-md-4" v-for="m in metricsList" :key="m.label">
        <div class="card shadow-sm">
          <div class="card-body">
            <div class="text-muted small">{{ m.label }}</div>
            <div class="fs-3">{{ m.value }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-3">
      <div class="card-body">
        <div class="row g-2">
          <div class="col">
            <input v-model="filters.q" class="form-control" placeholder="Search">
          </div>

          <div class="col">
            <select v-model="filters.location" class="form-select">
              <option disabled value="">Select Location</option>
              <option v-for="name in uniqueLocations" :key="name" :value="name">
                {{ name }}
              </option>
            </select>
          </div>

          <div class="col">
            <input v-model="filters.qr" class="form-control" placeholder="Ticket code">
          </div>

          <div class="col">
            <select v-model="filters.status" class="form-select">
              <option value="">Any Status</option>
              <option value="Requested">Requested</option>
              <option value="Delivering">Delivering</option>
              <option value="Partially Fulfilled">Partially Fulfilled</option>
              <option value="Fulfilled">Fulfilled</option>
            </select>
          </div>

          <div class="col-auto">
            <button class="btn btn-dark" @click="loadDonations">Filter</button>
          </div>
          <div class="col-auto">
            <button class="btn btn-outline-secondary" @click="resetFilters">Reset</button>
          </div>

        </div>
      </div>
    </div>

    <div class="list-group">
      <div v-for="d in paginatedDonations" :key="d.id"
        class="list-group-item d-flex justify-content-between align-items-center"
        @click="openDonation(d.unique_qr_code)" style="cursor:pointer;">
        <div>
          <div class="fw-semibold">{{ d.unique_qr_code }}</div>
          <div class="text-muted small">
            At {{ d.location }} • {{ formatSanDiegoDateTime(d.created_at) }}
          </div>
          <div class="small mt-1">
            <button v-if="hasComments(d.comments)" type="button" class="btn btn-link btn-sm p-0"
              @click.stop="openConversation(d.unique_qr_code)">
              View Conversation
            </button>
            <span v-else class="text-muted">-No Comments-</span>
          </div>
        </div>
        <span class="badge rounded-pill border px-3" :class="{
          'bg-primary text-light': donationStatus(d) === 'Fulfilled',
          'bg-success text-light': donationStatus(d) === 'Delivering',
          'bg-warning text-dark': donationStatus(d) === 'Requested',
          'bg-secondary text-light': donationStatus(d) === 'Partially Fulfilled'
        }">
          {{ donationStatus(d) }}
        </span>
      </div>
    </div>

    <nav class="mt-3">
      <ul class="pagination justify-content-center">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <button class="page-link" @click="prevPage">Previous</button>
        </li>
        <li class="page-item disabled">
          <span class="page-link">
            Page {{ currentPage }} of {{ totalPages }}
          </span>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <button class="page-link" @click="nextPage">Next</button>
        </li>
      </ul>
    </nav>


    <div v-if="showModal" class="modal d-block" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">

          <div class="modal-header">
            <h5 class="modal-title">Create New Order Request</h5>
            <button type="button" class="btn-close" @click="showModal = false"></button>
          </div>

          <div class="modal-body">

            <div class="mb-3">
              <select v-model="newDonation.location" class="form-select" :disabled="isAttendant"
                :class="{ 'opacity-50': isAttendant }">
                <option disabled value="">Select Location</option>
                <option v-for="name in uniqueLocations" :key="name" :value="name">
                  {{ name }}
                </option>
              </select>
            </div>







            <div v-for="entry in entries" :key="entry.uid"
              class="border rounded p-3 mb-3 d-flex align-items-center gap-2 flex-wrap">


              <select class="form-select" style="width: 180px" v-model="entry.category"
                @change="entry.category_name = ''">

                <option disabled value="">Select Category</option>
                <option v-for="cat in allowedCategories" :key="cat" :value="cat">
                  {{ formatCategoryName(cat) }}
                </option>
              </select>

              <select class="form-select" style="width: 220px" v-model="entry.category_id">

                <option disabled value="">Select Item</option>
                <option v-for="c in groupedCategories[entry.category]" :key="c.id" :value="c.category_name">
                  {{ cleanName(c.category_name) }}
                </option>
              </select>

              <input type="number" min="0" class="form-control" style="width: 90px" v-model.number="entry.quantity"
                placeholder="Quantity">

              <select class="form-select" style="width: 140px" v-model="entry.bin_type">
                <option disabled value="">Bin Type</option>
                <option v-for="option in getBinTypeOptions(entry.category, entry.category_id)" :key="option"
                  :value="option">
                  {{ option }}
                </option>
              </select>

              <button class="btn btn-outline-danger" @click="removeEntry(entry.uid)">
                <i class="bi bi-trash"></i>
              </button>




            </div>

            <button class="btn btn-outline-primary mb-3" @click="addEntry()">
              <i class="bi bi-plus-lg"></i> Add Item
            </button>

            <input v-model="newDonation.comments" class="form-control mb-3" placeholder="Comments" />


          </div>

          <div class="modal-footer">
            <button class="btn btn-dark" @click="createDonation">Submit</button>
          </div>

        </div>
      </div>
    </div>

  </div>


</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { formatSanDiegoDateTime } from '../utils/dateTime';

const router = useRouter();
const store = sessionStorage.getItem('location') || '';
const userType = (sessionStorage.getItem('userType') || '').toLowerCase();
const metricsList = ref([]);
const filters = ref({ q: '', location: '', qr: '', status: '' });
const donations = ref([]);
const showModal = ref(false);
const newDonation = ref({ location: '', comments: '', items: [] });
const locations = ref([]);
const categories = ref([]);
const groupedCategories = ref({});
const currentPage = ref(1);
const pageSize = ref(50);
const entries = ref([]);


const allowedCategories = [];

if (userType === 'attendant' && store !== 'Fixed Crew') {
  allowedCategories.push(
    'Requested_Product',
    'Product_For_Reallocation',
    'Product_On_Hand',
    'Donation_Info',
    'Delivered_Product',
    'Actually_Reallocated'
  );
}

if (userType === 'attendant' && store === 'Fixed Crew') {
  allowedCategories.push('Fixed_Crew');
}

if (userType === 'authorizer') {
  allowedCategories.push(
    'Requested_Product',
    'Product_For_Reallocation',
    'Product_On_Hand',
    'Donation_Info',
    'Delivered_Product',
    'Actually_Reallocated',
    'Fixed_Crew'
  );
}



const totalPages = computed(() => Math.ceil(donations.value.length / pageSize.value));
const paginatedDonations = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  return donations.value.slice(start, end);
});

function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
}
function prevPage() {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
}

function getAuthHeaders() {
  const token = sessionStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function donationStatus(donation) {
  return donation.computed_submission_status || donation.submission_status;
}

async function loadLocations() {
  const { data } = await axios.get(
    `${process.env.VUE_APP_API_URL}/api/locations`,
    { headers: getAuthHeaders() }
  );
  locations.value = data;
}

const uniqueLocations = computed(() => {
  return Array.from(new Set(locations.value.map(l => l.store_name)));
});

async function loadCategories() {
  const { data } = await axios.get(
    `${process.env.VUE_APP_API_URL}/api/categories`,
    { headers: getAuthHeaders() }
  );

  const filtered = data.filter(c => allowedCategories.includes(c.category));
  categories.value = filtered;

  const grouped = {};
  filtered.forEach(c => {
    if (!grouped[c.category]) grouped[c.category] = [];
    grouped[c.category].push(c);
  });
  groupedCategories.value = grouped;
}



function formatCategoryName(cat) {
  return cat.replace(/_/g, ' ');
}

function cleanName(name) {
  if (!name) return '';

  let cleaned = name.replace(/^#_of_/, '');

  const parts = cleaned.split('_');

  parts.pop();

  return parts.join(' ');
}

function hasComments(comments) {
  if (!comments) return false;
  try {
    const messages = typeof comments === 'string' ? JSON.parse(comments) : comments;
    if (Array.isArray(messages)) return messages.some(message => message.comment && message.comment.trim());
  } catch {
    return Boolean(String(comments).trim());
  }
  return Boolean(String(comments).trim());
}

function getBinTypeOptions(category, category_name) {
  if (category === 'CRT') {
    return ['CRT'];
  }

  if (category === 'Fixed_Crew') {
    const cleanedItem = cleanName(category_name);

    if (cleanedItem === 'Presort Text') {
      return ['Cage', 'Bin', 'Gaylord'];
    }
    if (cleanedItem === 'Presort Wares') {
      return ['Bin', 'Gaylord'];
    }
  }

  return ['Pallet', 'Gaylord', 'Bin', 'Loose and Large', 'Tote', 'CRT'];
}



function generateUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}




async function loadDonations() {
  const { data } = await axios.get(
    `${process.env.VUE_APP_API_URL}/api/donations`,
    { params: { ...filters.value, location: store, userType: userType }, headers: getAuthHeaders() }
  );

  donations.value = data;
}


async function resetFilters() {
  filters.value = { q: '', location: '', qr: '', status: '' };
  loadDonations();

}

async function loadMetrics() {
  const { data } = await axios.get(
    `${process.env.VUE_APP_API_URL}/api/metrics`,
    { headers: getAuthHeaders() }
  );
  metricsList.value = [
    { label: 'Total tickets', value: data.totalDonations },
    { label: 'Past due', value: data.pastDue },
    { label: 'Unfulfilled items', value: data.unfulfilledItems }
  ];
}




function addEntry() {
  entries.value.push({
    uid: generateUid(),
    category: "",
    category_id: "",
    quantity: 0,
    bin_type: "",
    bin_ids: ""
  });

}




function removeEntry(uid) {
  entries.value = entries.value.filter(e => e.uid !== uid);
}




async function createDonation() {
  const mergedItems = new Map();

  entries.value.forEach(entry => {
    const key = JSON.stringify([
      entry.category,
      entry.category_id,
      entry.bin_type
    ]);

    const existing = mergedItems.get(key);

    if (existing) {
      existing.quantity += Number(entry.quantity) || 0;
    } else {
      mergedItems.set(key, {
        ...entry,
        quantity: Number(entry.quantity) || 0
      });
    }
  });

  newDonation.value.items = Array.from(mergedItems.values());

  await axios.post(
    `${process.env.VUE_APP_API_URL}/api/donations`,
    {
      ...newDonation.value
    },
    {
      headers: getAuthHeaders()
    }
  );

  showModal.value = false;
  entries.value = [];
  newDonation.value = {
    location: isAttendant.value ? store : '',
    comments: '',
    items: []
  };

  await loadMetrics();
  await loadDonations();
}



async function openDonation(qr) {
  try {
    await axios.get(
      `${process.env.VUE_APP_API_URL}/api/donations/${qr}`,
      { headers: getAuthHeaders() }
    );
    if (userType === 'attendant') {
      router.push({ name: 'MyDonationDetail', params: { qr } });
    } else if (userType === 'authorizer') {
      router.push({ name: 'AdminTicketDetail', params: { qr } });
    } else {
      router.push({ name: 'DonationDetail', params: { code: qr } });
    }
  } catch (err) {
    console.error('Failed to open donation:', err);
  }
}

function openConversation(qr) {
  const routeName = userType === 'attendant'
    ? 'MyDonationDetail'
    : userType === 'authorizer'
      ? 'AdminTicketDetail'
      : 'DonationDetail';
  const params = routeName === 'DonationDetail' ? { code: qr } : { qr };
  router.push({ name: routeName, params, query: { messages: '1' } });
}



onMounted(() => {
  loadLocations();
  loadCategories();
  loadMetrics();
  loadDonations();
});

const isAttendant = computed(() => userType === 'attendant');

function openCreateModal() {
  if (isAttendant.value && store) {
    newDonation.value.location = store;
  }
  showModal.value = true;
}
</script>

<style>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>